// CHANGE-card: request early access to the ernd card. /card is the one status screen, /card/apply the
// steps. It's a request, not an application for credit: no decisions, ever (research.md 7). Words are in copy.ts.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError, useCurrentCompany, useMe, useRefreshMe, useSelectCompany } from '../../lib'
import { ErrorBox, Loading } from '../../app/ui'
import { PageBand } from '../../app/PageBand'
import { FieldInput, newPersonId, readyToSave, shownFor, unfinished } from './Fields'
import * as C from './copy'
import type { Answers, CardOverview, CardRequest, FieldConfig, FieldDef, Person, StepDef } from './types'
import './card.css'
import { SkyCard } from '../../app/SkyCard'
import { FocusFlow, screenGroups, screensOf, withDerived } from './Looks'
import { track, trackContext, useTrackErrors } from './track'

function useCardOn(): boolean | undefined {
  const { me } = useMe()
  return me ? me.features?.card === true : undefined
}

function CardOff() {
  return <div className="page stack" data-testid="card-off"><PageBand title={C.TITLE} sub={C.OFF} /></div>
}

/** A signed-in person with no company yet starts the request right here: the first screen's legal name and website
 *  create the company (founder, 2026-10-27: no setup chat, no rewards app). With early access switched off they get
 *  the company form, or the closed notice. */
function NoCompanyYet({ on, here }: { on: boolean | undefined; here?: boolean }) {
  const { me } = useMe()
  // A card-only person (or anyone but staff while CARD_ONLY_APP is on) has no company form: say it's closed instead.
  if (!on && (me?.card_only || (me?.features?.card_only_app && !me.is_staff))) return <CardOff />
  if (!on) return <Navigate to="/new" replace />
  return here ? <CardStart /> : <Navigate to="/card/apply" replace />
}

type BlankForm = {
  fields: FieldConfig
  prefill: { user_name: string | null; user_email: string | null; website: string | null; domain: string | null
    existing: { id: string; name: string }[] }
}

/** The first screen before any company or draft exists. Continue creates both, then the second screen autosaves. */
function CardStart() {
  const q = useQuery({ queryKey: ['card', 'form'], queryFn: () => api<BlankForm>('GET', '/api/card/form') })
  const refreshMe = useRefreshMe()
  const selectCompany = useSelectCompany()
  const qc = useQueryClient()
  const navigate = useNavigate()
  const [answers, setAnswers] = useState<Answers | null>(null)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [different, setDifferent] = useState(false)
  const { me } = useMe()
  const draftKey = me?.user ? `${START_KEY}:${me.user.id}` : null
  const [kept, setKept] = useState(false)
  const starting = useRef(false)
  trackContext({ off: !!me?.view_as })
  useTrackErrors(errors, asksOf(q.data?.fields))
  useEffect(() => {
    if (q.data && answers === null && draftKey) {
      const saved = readStart(draftKey)
      setKept(!!saved)
      setAnswers({ website: q.data.prefill.website, applicant_name: q.data.prefill.user_name, ...saved })
    }
  }, [q.data, draftKey]) // eslint-disable-line react-hooks/exhaustive-deps
  // Nothing reaches the server before Continue (it creates the company), so keep what they typed in this browser
  // until then: a reload or a closed tab no longer loses the first screen (CHANGE-054).
  useEffect(() => {
    if (answers && draftKey && writeStart(draftKey, answers, q.data?.prefill)) setKept(true)
  }, [answers, draftKey]) // eslint-disable-line react-hooks/exhaustive-deps
  const start = useMutation({
    mutationFn: () => api<CardRequest>('POST', '/api/card/start', {
      step: q.data?.fields.steps[1]?.key ?? null, answers: Object.fromEntries(Object.entries(answers ?? {}).map(([k, v]) => [k, v === '' ? null : v])),
      different_company: different,
    }),
    onSuccess: async (r) => {
      if (draftKey) clearStart(draftKey)          // the server has it all now
      selectCompany(r.company_id)
      await refreshMe()
      // One screen: Continue sends the request too (founder, 2026-11-01: no second screen).
      const next = q.data?.fields.steps[1]?.key
      trackContext({ applicationId: r.id })
      if (!next) { await api<CardRequest>('POST', `/api/card/requests/${r.id}/submit`, { notice_version: q.data!.fields.notice_version }); track('sent') }
      await qc.invalidateQueries({ queryKey: ['card'] })
      navigate(next ? `/card/apply?step=${next}` : '/card', { replace: true })
    },
    onSettled: () => { starting.current = false },
    onError: (e) => {
      // another tab or tap got there first: re-read who they are, and this page becomes that request
      if (e instanceof ApiError && e.code === 'has_company') { if (draftKey) clearStart(draftKey); void refreshMe(); return }
      if (e instanceof ApiError && e.status === 401) { void refreshMe(); return }   // the Shell asks them to sign in
      // a teammate put the company on ernd while this page was open: show the note
      if (e instanceof ApiError && e.code === 'company_exists') void qc.invalidateQueries({ queryKey: ['card', 'form'] })
      if (!(e instanceof ApiError && e.field)) track('error', { code: e instanceof ApiError ? e.code.slice(0, 40) : 'other' })
      if (e instanceof ApiError && e.field) {
        const field = e.field
        setErrors({ [field]: e.message })
        document.querySelector<HTMLElement>(`[data-testid="card-${field}"]`)?.focus()
      }
    },
  })
  if (q.error) return <ErrorBox error={q.error} />
  if (!q.data || !answers) return <Loading />
  const config = q.data.fields
  const step = config.steps[0]
  const existing = q.data.prefill.existing ?? []
  if (existing.length && !different) {
    // One request per company: their email domain already has a company on ernd. Its name only (the same hint the
    // app gives elsewhere), never whether it has sent a request.
    return (
      <div className="page stack card-apply" data-testid="card-apply" data-step="same-domain">
        <PageBand title={C.TITLE} sub={C.INTRO} />
        <section className="e-panel stack card-intro" data-testid="card-existing">
          <h2 className="e-heading">{C.SAME_DOMAIN_TITLE(existing.map((c) => c.name).join(', '))}</h2>
          <p>{C.SAME_DOMAIN_BODY(q.data.prefill.domain ?? '')}</p>
          <div><button type="button" className="e-btn" data-testid="card-existing-continue" onClick={() => setDifferent(true)}>{C.SAME_DOMAIN_CONTINUE}</button></div>
        </section>
      </div>
    )
  }
  const change = (f: FieldDef, v: unknown, picked?: () => void) => {
    setAnswers((now) => ({ ...now, [f.key]: v }))    // an autofill sets several boxes before React draws again
    if (errors[f.key]) setErrors((e) => { const n = { ...e }; delete n[f.key]; return n })
    if (picked && (f.kind === 'yes_no' || (f.kind === 'select' && (f.options ?? []).length <= 8))) picked()
  }
  const fields = step.fields.filter((f) => shownFor(f, withDerived(answers)))
  const oneScreen = config.steps.length === 1
  return (
    <div className="page card-apply card-look-focus" data-testid="card-apply" data-step={step.key}>
      <FocusFlow steps={config.steps} index={0} fields={fields} answers={answers}
        setError={(k, m) => setErrors((x) => ({ ...x, [k]: m }))}
        check={(f) => checkAnswer(f, answers)}
        input={(f, picked) => <FieldInput key={f.key} def={f} value={answers[f.key]} testid={`card-${f.key}`} error={errors[f.key]} onChange={(v) => change(f, v, picked)} />}
        onFinishStep={() => {
          if (starting.current) return                 // a double tap: the mutation's own state updates too late to catch it
          const missing = gateMissing(config, answers)
          if (Object.keys(missing).length) { setErrors(missing); return }
          starting.current = true
          start.mutate()
        }}
        focusKey={Object.keys(errors)[0] ?? null}
        saveLine={<Secure label={kept ? C.SAVED_HERE : C.SAVED} />} finishLabel={oneScreen ? C.SEND : 'Continue'} busy={start.isPending}
        footer={start.error && !(start.error instanceof ApiError && (start.error.field || start.error.code === 'has_company' || start.error.status === 401)) ? <ErrorBox error={start.error} /> : undefined}
        welcome={{ title: C.TITLE, sub: C.INTRO }}
        groups={screenGroups(liveSteps(config.steps, answers))} />
    </div>
  )
}

/** Each question's "answer this" sentence, so a missing answer is noted as `required` (track.ts). */
function asksOf(config?: FieldConfig): Record<string, string | undefined> {
  return Object.fromEntries((config?.steps ?? []).flatMap((s) => s.fields).map((f) => [f.key, f.ask]))
}

/** The steps this person will see, with only the questions their answers show (a sole owner has no people step). */
function liveSteps(steps: StepDef[], answers: Answers): StepDef[] {
  const a = withDerived(answers)
  return steps.map((s) => ({ ...s, fields: s.fields.filter((f) => shownFor(f, a)) })).filter((s) => screensOf(s.fields).length)
}

/** One answer through the server's own rules (POST /api/card/check), so a bad phone or address stops on its own
 *  screen, never at Send. A network hiccup lets them through: Send still checks everything. */
async function checkAnswer(f: FieldDef, answers: Answers): Promise<string | null> {
  if (f.kind === 'people') {
    const people = (answers[f.key] as Person[] | undefined) ?? []
    for (const [i, person] of people.entries()) {
      for (const sub of f.fields ?? []) {
        const v = person[sub.key as keyof Person]
        if (sub.required && (v === undefined || v === null || v === '')) return `Owner ${i + 1}: ${sub.ask || 'answer ' + sub.label.toLowerCase()}.`
      }
    }
  }
  if (f.sensitive && (answers[f.key] === undefined || answers[f.key] === null || answers[f.key] === '')) return null   // on file already
  try {
    const r = await api<{ ok: boolean; message?: string }>('POST', '/api/card/check', { key: f.key, value: answers[f.key] ?? null, answers })
    return r.ok ? null : r.message ?? 'Check this answer.'
  } catch (e) {
    return e instanceof ApiError && e.status === 400 && e.message ? e.message : null
  }
}

// ---------- the first screen, kept in this browser until Continue (CHANGE-054) ----------

const START_KEY = 'ernd-card-start'
const START_DAYS = 30

/** What they typed on the first screen, or null. Storage can be off or full: then it's just not kept. */
function readStart(key: string): Answers | null {
  try {
    const raw = window.localStorage.getItem(key)
    if (!raw) return null
    const v = JSON.parse(raw) as { at: number; answers: Answers }
    if (!v?.answers || typeof v.at !== 'number' || Date.now() - v.at > START_DAYS * 86_400_000) { window.localStorage.removeItem(key); return null }
    return v.answers
  } catch { return null }
}

/** Keeps the answers that differ from the prefill. True when something was kept. */
function writeStart(key: string, answers: Answers, prefill?: BlankForm['prefill']): boolean {
  const typed = Object.fromEntries(Object.entries(answers).filter(([k, v]) => v !== null && v !== undefined && v !== ''
    && !(k === 'website' && v === prefill?.website) && !(k === 'applicant_name' && v === prefill?.user_name)))
  try {
    if (!Object.keys(typed).length) { window.localStorage.removeItem(key); return false }
    window.localStorage.setItem(key, JSON.stringify({ at: Date.now(), answers: typed }))
    return true
  } catch { return false }
}

function clearStart(key: string) {
  try { window.localStorage.removeItem(key) } catch { /* nothing kept */ }
}

export function useCardOverview(companyId: string | undefined, on: boolean | undefined) {
  return useQuery({
    queryKey: ['card', companyId],
    queryFn: () => api<CardOverview>('GET', `/api/companies/${companyId}/card`),
    enabled: !!companyId && on === true,
  })
}

/** The first screen's key answers still empty (founder, 2026-10-31: no second screen without them). */
function gateMissing(config: FieldConfig, answers: Answers): Record<string, string> {
  const defs = config.steps[0].fields
  return Object.fromEntries((config.gate ?? []).filter((k) => answers[k] === undefined || answers[k] === null || String(answers[k]).trim() === '')
    .map((k) => [k, defs.find((f) => f.key === k)?.ask || 'Required.']))
}

function focusFirst(errors: Record<string, string>) {
  const k = Object.keys(errors)[0]
  if (!k) return
  const box = document.querySelector<HTMLElement>(`[data-testid="card-${k}-field"]`) ?? document.querySelector<HTMLElement>(`[data-testid="card-${k}"]`)
  const el = box?.matches('input, select, textarea') ? box : box?.querySelector<HTMLElement>('input, select, textarea')
  ;(el ?? box)?.scrollIntoView({ block: 'center' })
  el?.focus({ preventScroll: true })
}

// ---------- status ----------

export function CardPage() {
  const on = useCardOn()
  const { me } = useMe()
  const company = useCurrentCompany()
  const q = useCardOverview(company?.id, on)
  const qc = useQueryClient()
  const navigate = useNavigate()
  const keep = useMutation({
    mutationFn: (id: string) => api<CardRequest>('POST', `/api/card/requests/${id}/keep`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['card'] }),
  })
  if (me && !company) return <NoCompanyYet on={on} />
  if (on === false) return <CardOff />
  if (q.isLoading || !q.data) return q.error ? <ErrorBox error={q.error} /> : <Loading />
  const { request: a, can_apply } = q.data
  const name = company!.name
  // Nothing started: an admin goes straight to the form.
  if (!a && can_apply) return <Navigate to="/card/apply" replace />
  // founder, 2026-10-31: the home is the company's name, the card and where the request stands. Nothing else.
  const status = a && a.status !== 'withdrawn' ? a.status : 'none'
  return (
    <div className="page card-home" data-testid="card-page" data-status={a?.status ?? 'none'}>
      <h1 className={`card-home-name${name.length > 24 ? ' card-home-name-long' : ''}`} title={name} data-testid="card-home-name">{name}</h1>
      <div className="card-home-card"><SkyCard /></div>
      <section className="card-home-status">
        <h2 className="card-home-title">{C.NAME}</h2>
        <p data-testid="card-status-line"><span data-testid="card-status">{C.STATE_LINE[status]}</span></p>
        {a?.full && a.expiry_notice_at ? (
          <div className="stack">
            <p data-testid="card-keep-line">{C.KEEP_LINE}</p>
            <div><button className="e-btn e-btn-primary" data-testid="card-keep" disabled={keep.isPending} onClick={() => keep.mutate(a.id)}>{C.KEEP}</button></div>
          </div>
        ) : null}
        {status === 'draft' && a?.full ? <div><Link className="e-btn e-btn-primary e-btn-lg" to="/card/apply" data-testid="card-continue">{C.CONTINUE}</Link></div> : null}
        {status === 'none' && can_apply ? <div><button className="e-btn e-btn-primary e-btn-lg" data-testid="card-start" onClick={() => navigate('/card/apply')}>{C.START}</button></div> : null}
        {(status === 'none' || status === 'draft') && !can_apply ? <p className="muted" data-testid="card-ask-admin">{C.ADMIN_ONLY}</p> : null}
      </section>
    </div>
  )
}

/** Someone who sent a request gets a personal link to pass on (CHANGE-065). It lives on Profile, because the card home
 *  holds the name, the card and the status and nothing else. Nothing shows for anyone who hasn't sent one. The URL stays
 *  visible for when copying fails. */
export function InviteLink() {
  const on = useCardOn()
  const company = useCurrentCompany()
  const url = useCardOverview(company?.id, on).data?.invite_url
  const [copied, setCopied] = useState(false)
  if (!url) return null
  const copy = () => { navigator.clipboard.writeText(url).then(() => setCopied(true)).catch(() => {}) }
  return (
    <section className="stack" data-testid="card-invite">
      <p>{C.INVITE}</p>
      <div className="prompt-box">
        <code data-testid="card-invite-url">{url.replace(/^https?:\/\//, '')}</code>
        <button type="button" data-testid="card-invite-copy" onClick={copy}>{copied ? C.INVITE_COPIED : C.INVITE_COPY}</button>
      </div>
    </section>
  )
}

// ---------- the steps ----------

type SaveState = 'idle' | 'saving' | 'saved' | 'error' | 'offline'

const RETRY_MS = 5000

/** Autosave: changed answers go up about a second after the last keystroke, one request at a time, and as the page
 *  goes away. Values the server would refuse while still being typed (a half EIN) wait until they are whole. A save
 *  that can't reach the server tries again by itself. The EIN leaves the page once the server has it: from then on it
 *  shows only "ending 6789". */
function useAutosave(app: CardRequest | undefined, config: FieldConfig | undefined) {
  const qc = useQueryClient()
  const refreshMe = useRefreshMe()
  const [answers, setAnswers] = useState<Answers>({})
  const [server, setServer] = useState<CardRequest | undefined>(app)
  const [state, setState] = useState<SaveState>('idle')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [formError, setFormError] = useState<unknown>(null)
  const dirty = useRef<Set<string>>(new Set())
  const latest = useRef<Answers>({})
  const busy = useRef<Promise<void> | null>(null)
  const timer = useRef<number | undefined>(undefined)
  const defs = useMemo(() => Object.fromEntries((config?.steps ?? []).flatMap((s) => s.fields).map((f) => [f.key, f])), [config])
  const loaded = useRef<string | null>(null)
  const failed = useRef<string | null>(null)        // the field the last save was refused on
  const flying = useRef<string[]>([])               // the answers in the save that's running
  const gone = useRef(false)                        // the page has left: no more retries

  useEffect(() => {
    if (app && loaded.current !== app.id) {
      loaded.current = app.id
      latest.current = { ...(app.answers ?? {}) }
      setAnswers(latest.current)
      setServer(app)
    }
  }, [app])

  const send = useCallback(async (step?: string, keepalive?: boolean): Promise<boolean> => {
    if (!server) return false
    // The page is going, so nothing can wait. The save that's running may die with it, so its answers go again.
    if (!keepalive) while (busy.current) await busy.current
    const keys = [...new Set([...dirty.current, ...(keepalive ? flying.current : [])])].filter((k) => defs[k] && readyToSave(defs[k], latest.current[k]))
    if (!keys.length && !step) return true
    const body: Answers = {}
    for (const k of keys) body[k] = latest.current[k] === '' ? null : latest.current[k]
    keys.forEach((k) => dirty.current.delete(k))
    setState('saving')
    failed.current = null
    flying.current = keys
    let ok = true
    busy.current = (async () => {
      try {
        const r = await api<CardRequest>('PUT', `/api/card/requests/${server.id}`, { step: step ?? null, answers: body }, { keepalive })
        setServer(r)
        setErrors((e) => { const n = { ...e }; keys.forEach((k) => delete n[k]); return n })
        setFormError(null)
        const next = { ...latest.current }
        for (const k of keys) if (defs[k].sensitive && next[k] === body[k]) delete next[k]   // the EIN leaves the page
        latest.current = next
        setAnswers(next)
        // answers still being typed (a half EIN) don't count as unsaved work yet
        setState([...dirty.current].some((k) => defs[k] && readyToSave(defs[k], latest.current[k])) ? 'saving' : 'saved')
        qc.setQueryData(['card', 'request', r.id], r)
        if (keys.includes('legal_name')) void refreshMe()      // the company's name follows it (the card home, the switcher)
      } catch (e) {
        ok = false
        keys.forEach((k) => dirty.current.add(k))
        if (e instanceof ApiError && e.status === 401) { setState('idle'); void refreshMe() }        // the Shell asks them to sign in
        else if (e instanceof ApiError && e.code === 'not_draft') { setState('idle'); void qc.invalidateQueries({ queryKey: ['card'] }) }  // sent from another tab: on to the status page
        else if (e instanceof ApiError && (e.code === 'network' || e.status >= 500)) {
          setState('offline')
          setFormError(e)
          if (!gone.current) {
            window.clearTimeout(timer.current)
            timer.current = window.setTimeout(() => { void send() }, RETRY_MS)
          }
        } else {
          setState('error')
          if (e instanceof ApiError && e.field) {
            const field = e.field
            failed.current = field
            setErrors((x) => ({ ...x, [field]: e.message }))
          } else setFormError(e)
        }
      } finally {
        if (flying.current === keys) flying.current = []
      }
    })()
    await busy.current
    busy.current = null
    return ok
  }, [server, defs, qc, refreshMe])

  const change = useCallback((key: string, v: unknown) => {
    latest.current = { ...latest.current, [key]: v }
    setAnswers(latest.current)
    dirty.current.add(key)
    setState(defs[key] && readyToSave(defs[key], v) ? 'saving' : 'idle')
    window.clearTimeout(timer.current)
    timer.current = window.setTimeout(() => { void send() }, 1000)
  }, [send, defs])

  // The last answer goes up as the page goes away: a refresh, a closed tab, a phone switching apps (CHANGE-063).
  useEffect(() => {
    const flush = () => { window.clearTimeout(timer.current); void send(undefined, true) }
    const hidden = () => { if (document.visibilityState === 'hidden') flush() }
    window.addEventListener('pagehide', flush)
    document.addEventListener('visibilitychange', hidden)
    return () => { window.removeEventListener('pagehide', flush); document.removeEventListener('visibilitychange', hidden) }
  }, [send])

  useEffect(() => {
    gone.current = false
    return () => { gone.current = true; window.clearTimeout(timer.current) }
  }, [])
  // Answers the page holds back. One on a question no longer shown can't stop anything: it was never going up.
  // "Shown" uses the derived answers too: a new job title can hide the person-in-charge lines (withDerived).
  const pending = () => {
    const shown = withDerived(latest.current)
    return [...dirty.current].filter((k) => defs[k] && shownFor(defs[k], shown) && !readyToSave(defs[k], latest.current[k]))
  }
  /** Puts the sentence on a field that can't go up as it stands. */
  const mark = (key: string) => {
    const why = defs[key] ? unfinished(defs[key], latest.current[key]) : null
    if (why) setErrors((e) => ({ ...e, [key]: why }))
  }
  return { answers, server, state, errors, setErrors, formError, change, send, pending, mark, failed }
}

export function ApplyPage() {
  const on = useCardOn()
  const { me } = useMe()
  const company = useCurrentCompany()
  const ov = useCardOverview(company?.id, on)
  const qc = useQueryClient()
  const refreshMe = useRefreshMe()
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const current = ov.data?.request
  const app = current && current.status === 'draft' ? current : undefined
  const startable = !!ov.data && ov.data.can_apply && (!current || current.status === 'withdrawn')
  const start = useMutation({
    mutationFn: () => api<CardRequest>('POST', `/api/companies/${company!.id}/card`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['card', company?.id] }),
  })
  // Arriving with nothing started (from ernd.io, the dashboard or straight after signup): start the draft.
  const tried = useRef(false)
  useEffect(() => {
    if (startable && !tried.current) { tried.current = true; start.mutate() }
  }, [startable]) // eslint-disable-line react-hooks/exhaustive-deps
  const config = ov.data?.fields
  const save = useAutosave(app, config)
  trackContext({ applicationId: app?.id ?? null, off: !!me?.view_as })
  useTrackErrors(save.errors, asksOf(config))
  const [jump, setJump] = useState<string | null>(null)
  const [startEnd, setStartEnd] = useState(false)
  const rawStep = params.get('step') ?? save.server?.step ?? config?.steps[0]?.key
  const stepKey = rawStep === 'more' ? 'business' : rawStep      // a draft or link from before the five screens
  // The URL always names the step, so the browser's Back goes to the step before.
  useEffect(() => {
    if (save.server && !params.get('step') && stepKey) setParams({ step: stepKey }, { replace: true })
  }, [save.server?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  // "Still needed" and a failed send take them to the field itself: scrolled into view and focused.
  const [focusField, setFocusField] = useState<string | null>(null)
  useEffect(() => {
    if (!focusField) return
    const id = requestAnimationFrame(() => {
      const box = document.querySelector<HTMLElement>(`[data-testid="card-${focusField}-field"]`)
        ?? document.querySelector<HTMLElement>(`[data-testid="card-${focusField}"]`)
      const el = box?.matches('input, select, textarea') ? box : box?.querySelector<HTMLElement>('input, select, textarea, button')
      ;(el ?? box)?.scrollIntoView({ block: 'center' })
      el?.focus({ preventScroll: true })
      setFocusField(null)
    })
    return () => cancelAnimationFrame(id)
  }, [focusField, stepKey])
  const showField = (key: string) => {
    const home = config?.steps.find((s) => s.fields.some((f) => f.key === key))?.key
    if (home && home !== stepKey) setParams({ step: home })
    setFocusField(key)
  }
  const send = useMutation({
    mutationFn: async () => {
      // Nothing goes out with an answer the page couldn't save: the older one would be sent in its place (CHANGE-063).
      const left = save.pending()
      if (left.length) {
        left.forEach(save.mark)
        showField(left[0])
        return null
      }
      if (!(await save.send())) { if (save.failed.current) showField(save.failed.current); return null }
      return api<CardRequest>('POST', `/api/card/requests/${save.server!.id}/submit`, { notice_version: config!.notice_version })
    },
    onSuccess: (sent) => {
      if (!sent) return
      track('sent')
      // The status page shows this straight away, however slow the re-read is.
      qc.setQueryData<CardOverview>(['card', company?.id], (o) => (o ? { ...o, request: sent } : o))
      void qc.invalidateQueries({ queryKey: ['card'] })
      navigate('/card')
    },
    onError: (e) => {
      track('error', { code: e instanceof ApiError ? e.code.slice(0, 40) : 'other' })
      if (e instanceof ApiError && e.status === 401) { void refreshMe(); return }
      if (e instanceof ApiError && e.code === 'not_draft') { void qc.invalidateQueries({ queryKey: ['card'] }); return }
      // a required answer is missing: take them to it on the first screen
      if (e instanceof ApiError && e.code === 'incomplete') {
        // mark every missing answer with its own sentence, and go to the first one
        void api<CardRequest>('GET', `/api/card/requests/${save.server!.id}`).then((r) => {
          const missing = r.problems ?? []
          save.setErrors((x) => ({ ...x, ...Object.fromEntries(missing.map((p) => [p.field, p.message])) }))
          const first = missing[0]?.step ?? config?.steps.find((s) => s.fields.some((f) => f.key === e.field))?.key
          if (first && first !== stepKey) setParams({ step: first })
          setFocusField(missing[0]?.field ?? e.field ?? null); setJump(missing[0]?.field ?? e.field ?? null)
        })
      }
    },
  })

  if (me && !company) return <NoCompanyYet on={on} here />
  if (on === false) return <CardOff />
  if (ov.error) return <ErrorBox error={ov.error} />
  if (start.error) return <div className="page stack"><PageBand title={C.TITLE} /><ErrorBox error={start.error} /><p><Link className="e-link" to="/card">Back</Link></p></div>
  if (!ov.data || !config) return <Loading />
  if (!ov.data.can_apply) return <Navigate to="/card" replace />
  if (current && current.status !== 'draft' && !startable) return <Navigate to="/card" replace />
  if (!app || !save.server) return <Loading what="Starting your request" />

  const steps = liveSteps(config.steps, save.answers)
  const index = Math.max(0, steps.findIndex((s) => s.key === stepKey))
  const step = steps[index]
  const last = index === steps.length - 1
  const go = async (to: StepDef, field?: string) => {
    if (index === 0 && to.key !== step.key) {
      const missing = gateMissing(config, save.answers)
      if (Object.keys(missing).length) {
        save.setErrors((x) => ({ ...x, ...missing }))
        requestAnimationFrame(() => focusFirst(missing))
        return
      }
    }
    const ok = await save.send(to.key)
    if (!ok) return
    setParams({ step: to.key })
    if (field) { setFocusField(field); setJump(field) }
    else window.scrollTo(0, 0)
  }
  const saveLine = save.state === 'saving' ? 'Saving…' : save.state === 'offline' ? C.RETRYING
    : save.state === 'error' ? 'Not saved. Check the answers marked below.' : save.pending().length ? C.NOT_SAVED : C.SAVED
  const pre = ov.data.prefill
  // The first owner starts as the signed-in person; they can change it or remove it.
  const nextOwner = (): Person => {
    const [first, ...rest] = String(save.answers.applicant_name ?? pre?.user_name ?? '').trim().split(/\s+/)
    const people = (save.answers.owners as Person[] | undefined) ?? []
    return people.length === 0
      ? { id: newPersonId(), first_name: first || null, last_name: rest.join(' ') || null, title: save.answers.applicant_title ?? null,
          email: pre?.user_email ?? null, is_me: true }
      : { id: newPersonId() }
  }
  const shown = step.fields
  const problems = save.server.problems ?? []
  const input = (f: FieldDef, picked?: () => void) => (
    <FieldInput def={f} value={save.answers[f.key]} testid={`card-${f.key}`}
      onChange={(v) => { save.change(f.key, v); if (save.errors[f.key]) save.setErrors((e) => { const n = { ...e }; delete n[f.key]; return n })
        if (picked && (f.kind === 'yes_no' || (f.kind === 'select' && (f.options ?? []).length <= 8))) picked() }}
      onBlur={() => { save.mark(f.key); void save.send() }}
      hint={f.sensitive ? save.server!.ein : null}
      newPerson={f.kind === 'people' ? nextOwner : undefined}
      error={save.errors[f.key]} />
  )

  const sendFooter = (
    <>
      {problems.length && send.isError ? (
        <div data-testid="card-problems">
          <p className="card-label">{C.STILL_NEEDED}</p>
          <ul className="card-problems">
            {problems.map((p, i) => (
              <li key={i}><button type="button" className="e-btn-text" onClick={() => go(steps.find((s) => s.key === p.step)!, p.field)}>{p.message}</button></li>
            ))}
          </ul>
        </div>
      ) : null}
      {save.formError ? <ErrorBox error={save.formError} /> : null}
      {send.error && !(send.error instanceof ApiError && (send.error.code === 'incomplete' || send.error.code === 'not_draft' || send.error.status === 401)) ? <ErrorBox error={send.error} /> : null}
    </>
  )
  return (
    <div className="page card-apply card-look-focus" data-testid="card-apply" data-step={step.key}>
      <FocusFlow steps={steps} index={index} fields={shown} answers={save.answers}
        setError={(k, m) => save.setErrors((x) => ({ ...x, [k]: m }))}
        input={(f, picked) => input(f, picked)}
        check={async (f) => { const bad = await checkAnswer(f, save.answers); if (!bad) await save.send(); return bad }}
        onFinishStep={async () => { setStartEnd(false); if (last) send.mutate(); else await go(steps[index + 1]) }}
        onBackStep={index > 0 ? () => { setStartEnd(true); void go(steps[index - 1]) } : undefined}
        startAtEnd={startEnd} focusKey={jump}
        saveLine={<Secure label={saveLine || C.SAVED} />} finishLabel={C.SEND} footer={sendFooter} busy={send.isPending}
        groups={screenGroups(steps)}
        onFile={save.server?.ein ? steps.flatMap((x) => x.fields).filter((f) => f.sensitive).map((f) => f.key) : []} />
    </div>
  )
}

/** The planned-points sentence and each perk partner's line (Firecrawl: 2x usage limits, no points), word for word
 *  from the server (pr/card/rewards.py). */
function Planned({ config, testid, className = 'card-planned' }: { config: FieldConfig; testid: string; className?: string }) {
  return (
    <div className="card-planned-block" data-testid={testid}>
      <p className={className}>{config.copy.planned_rewards}</p>
      {(config.partner_perks ?? []).map((line) => <p key={line} className={className} data-testid={`${testid}-perk`}>{line}</p>)}
    </div>
  )
}

/** The lucide "lock" icon, inline (ISC licence): one icon isn't worth a dependency. */
function LockIcon() {
  return (
    <svg className="card-secure-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  )
}

/** Under the header: the lock and "Saved" on one line (founder, 2026-10-31: the lock says encrypted). */
function Secure({ label = C.SAVED }: { label?: string }) {
  return (
    <span className="card-secure" data-testid="card-secure">
      <LockIcon />
      <span data-testid="card-save-state" aria-live="polite">{label}</span>
    </span>
  )
}

// ---------- the dashboard entry ----------

/** One panel on the overview, only when CARD_APPLICATIONS_ENABLED is on. */
export function CardEntry({ companyId }: { companyId: string }) {
  const on = useCardOn()
  const q = useCardOverview(companyId, on)
  if (on !== true || !q.data) return null
  const a = q.data.request
  const status = a && a.status !== 'withdrawn' ? a.status : null
  const text = status ? C.ENTRY[status] : q.data.can_apply ? C.ENTRY.none : C.ENTRY.member
  return (
    <section className="e-panel card-entry" data-testid="card-entry" data-status={status ?? 'none'}>
      <div className="card-entry-text">
        <h2 className="e-heading">{C.NAME}</h2>
        <p>{text}</p>
        {!status && q.data.can_apply ? <Planned config={q.data.fields} testid="card-entry-planned" className="card-help" /> : null}
      </div>
      {status === 'draft' && q.data.can_apply ? <Link className="e-btn e-btn-primary" to="/card/apply" data-testid="card-entry-continue">{C.CONTINUE}</Link>
        : status ? <Link className="e-btn" to="/card" data-testid="card-entry-status">{C.SEE}</Link>
        : q.data.can_apply ? <Link className="e-btn e-btn-primary" to="/card/apply" data-testid="card-entry-apply">{C.SHORT}</Link> : null}
    </section>
  )
}
